import { logger } from '@springtree/eva-sdk-core-logger';
import { useCallback } from 'react';
import getEvaEndpoint from '../helpers/get-eva-endpoint.js';
import userExpiration from '../helpers/user-expiration.js';
import currentUserTokenState from '../state/current-user/current-user-token-state.js';
import evaEndpointUrlState from '../state/core/eva-endpoint-url-state.js';
import requestedOrganizationUnitIdState from '../state/core/requested-organization-unit-id-state.js';
import requestedOrganizationUnitQueryState from '../state/core/requested-organization-unit-query-state.js';
import useGetState from './use-get-state.js';
/**
 * This hook is a convenience for calling services on an EVA endpoint with
 * the current endpoint and user token from the recoil state.
 *
 * @template SVC
 * @param {new() => SVC} service The service to call
 * @returns
 */
const useCallService = ({ service, }) => {
    const evaEndpointUrl = useGetState(evaEndpointUrlState);
    const currentUserToken = useGetState(currentUserTokenState);
    const evaRequestedOrganizationUnitID = useGetState(requestedOrganizationUnitIdState);
    const evaRequestedOrganizationUnitQuery = useGetState(requestedOrganizationUnitQueryState);
    const callService = useCallback(async (requestPayload, callOptions, hookOptions) => {
        try {
            if (!evaEndpointUrl) {
                throw new Error('No current EVA endpoint');
            }
            const evaEndpoint = await getEvaEndpoint(evaEndpointUrl);
            // Options are combined in such a way that the caller can override
            // the token if desired
            //
            const response = await evaEndpoint?.callService(service, requestPayload, {
                authenticationToken: currentUserToken,
                requestedOrganizationUnitID: evaRequestedOrganizationUnitID,
                requestedOrganizationUnitQuery: evaRequestedOrganizationUnitQuery,
                ...callOptions,
            });
            if (response && hookOptions?.onSuccess) {
                hookOptions.onSuccess(response);
            }
            return response;
        }
        catch (error) {
            if (currentUserToken && error.response?.status === 401) {
                logger.warn(`[${service.name}] User token has expired`, error)();
                userExpiration.tokenExpired(currentUserToken);
            }
            else {
                logger.error(`[${service.name}] Failed to call service`, error)();
            }
            if (hookOptions?.onError) {
                hookOptions.onError(error);
            }
        }
    }, [
        currentUserToken,
        evaEndpointUrl,
        evaRequestedOrganizationUnitID,
        evaRequestedOrganizationUnitQuery,
        service,
    ]);
    return callService;
};
export default useCallService;
//# sourceMappingURL=use-call-service.js.map